import { useState, useMemo, useCallback, useRef, Fragment } from 'react';
import {
  Table2, Download, RefreshCw, X, Plus, ChevronDown, ChevronRight,
  Filter, Settings2, BarChart3, GripVertical, FileText,
} from 'lucide-react';
import StatementView from '@/features/cash-flow/StatementView';
import { useCurrency } from '@/context/CurrencyContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useFetch } from '@/lib/fin/use-fetch';
import { cn } from '@/lib/utils';

/* ============================================================
   TYPES
   ============================================================ */

type DataSourceKey = 'cashflow' | 'balances' | 'pl' | 'budget';
type AggregationType = 'sum' | 'count' | 'avg';

interface PivotConfig {
  rowDims: string[];
  colDims: string[];
  valueDim: string;
  aggregation: AggregationType;
  showRowTotals: boolean;
  showColTotals: boolean;
  showGrandTotal: boolean;
  filters: Record<string, string[]>;
  dataSource: DataSourceKey;
}

interface AnalyticsData {
  cashflow: Record<string, any>[];
  balances: Record<string, any>[];
  pl: Record<string, any>[];
  budget: Record<string, any>[];
}

interface PivotResult {
  rowKeys: string[][];
  colKeys: string[];
  cells: Record<string, Record<string, number>>;
  rowTotals: Record<string, number>;
  colTotals: Record<string, number>;
  grandTotal: number;
}

/* ============================================================
   CONSTANTS
   ============================================================ */

const MONTH_ORDER = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const DATA_SOURCE_LABELS: Record<DataSourceKey, string> = {
  cashflow: 'Cash Flow',
  balances: 'Balances',
  pl: 'P&L',
  budget: 'Budget',
};

const DIMENSIONS_BY_SOURCE: Record<DataSourceKey, { key: string; label: string }[]> = {
  cashflow: [
    { key: 'entity', label: 'Entity' },
    { key: 'group', label: 'Group' },
    { key: 'type', label: 'Type' },
    { key: 'category', label: 'Category' },
    { key: 'source', label: 'Source' },
    { key: 'status', label: 'Status' },
    { key: 'payment_method', label: 'Payment Method' },
    { key: 'month', label: 'Month' },
    { key: 'year', label: 'Year' },
  ],
  balances: [
    { key: 'entity', label: 'Entity' },
    { key: 'group', label: 'Group' },
    { key: 'month', label: 'Month' },
    { key: 'year', label: 'Year' },
    { key: 'fiat_currency', label: 'Fiat Currency' },
  ],
  pl: [
    { key: 'entity', label: 'Entity' },
    { key: 'group', label: 'Group' },
    { key: 'metric_type', label: 'Metric Type' },
    { key: 'metric', label: 'Metric' },
    { key: 'month', label: 'Month' },
  ],
  budget: [
    { key: 'entity', label: 'Entity' },
    { key: 'group', label: 'Group' },
    { key: 'line_type', label: 'Line Type' },
    { key: 'category', label: 'Category' },
    { key: 'month', label: 'Month' },
    { key: 'year', label: 'Year' },
  ],
};

const VALUE_DIMS_BY_SOURCE: Record<DataSourceKey, { key: string; label: string }[]> = {
  cashflow: [{ key: 'amount', label: 'Amount' }],
  balances: [
    { key: 'total_balance', label: 'Total Balance' },
    { key: 'fiat_balance', label: 'Fiat Balance' },
    { key: 'crypto_balance', label: 'Crypto Balance' },
  ],
  pl: [{ key: 'amount', label: 'Amount' }],
  budget: [{ key: 'amount', label: 'Amount' }],
};

/* ============================================================
   PIVOT ENGINE
   ============================================================ */

function sortDimValues(dim: string, values: string[]): string[] {
  if (dim === 'month') {
    return [...values].sort((a, b) => MONTH_ORDER.indexOf(a) - MONTH_ORDER.indexOf(b));
  }
  if (dim === 'year') {
    return [...values].sort((a, b) => Number(a) - Number(b));
  }
  return [...values].sort((a, b) => a.localeCompare(b));
}

function computePivot(data: Record<string, any>[], config: PivotConfig): PivotResult {
  // 1. Apply filters
  let filtered = data;
  for (const [dim, values] of Object.entries(config.filters)) {
    if (values.length > 0) {
      filtered = filtered.filter(d => values.includes(String(d[dim] ?? '')));
    }
  }

  // 2. Build composite row/col keys
  const rowKeyMap = new Map<string, string[]>();
  const colKeySet = new Set<string>();
  const cellMap: Record<string, Record<string, number[]>> = {};

  for (const row of filtered) {
    const rowParts = config.rowDims.map(d => String(row[d] ?? 'N/A'));
    const rowKey = rowParts.join('|||');

    // For col dims, build composite col key
    const colParts = config.colDims.map(d => String(row[d] ?? 'N/A'));
    const colKey = colParts.join('|||');

    if (!rowKeyMap.has(rowKey)) rowKeyMap.set(rowKey, rowParts);
    colKeySet.add(colKey);

    if (!cellMap[rowKey]) cellMap[rowKey] = {};
    if (!cellMap[rowKey][colKey]) cellMap[rowKey][colKey] = [];
    cellMap[rowKey][colKey].push(Number(row[config.valueDim]) || 0);
  }

  // 3. Sort col keys
  let colKeys: string[];
  if (config.colDims.length === 1 && (config.colDims[0] === 'month' || config.colDims[0] === 'year')) {
    colKeys = sortDimValues(config.colDims[0], [...colKeySet]);
  } else if (config.colDims.length >= 2) {
    // Multi-level: sort by first dim then by second dim within each group
    const keys = [...colKeySet];
    keys.sort((a, b) => {
      const aParts = a.split('|||');
      const bParts = b.split('|||');
      for (let i = 0; i < config.colDims.length; i++) {
        const dim = config.colDims[i];
        const sorted = sortDimValues(dim, [aParts[i] || '', bParts[i] || '']);
        if (sorted[0] !== sorted[1]) return sorted[0] === aParts[i] ? -1 : 1;
      }
      return 0;
    });
    colKeys = keys;
  } else {
    colKeys = [...colKeySet].sort();
  }

  // 4. Sort row keys
  const rowEntries = [...rowKeyMap.entries()].sort((a, b) => {
    for (let i = 0; i < a[1].length; i++) {
      const dim = config.rowDims[i];
      const sorted = sortDimValues(dim, [a[1][i], b[1][i]]);
      if (sorted[0] !== sorted[1]) return sorted[0] === a[1][i] ? -1 : 1;
    }
    return 0;
  });

  const rowKeys = rowEntries.map(([, parts]) => parts);

  // 5. Aggregate
  const aggregate = (vals: number[]): number => {
    if (!vals || vals.length === 0) return 0;
    if (config.aggregation === 'count') return vals.length;
    if (config.aggregation === 'avg') return vals.reduce((s, v) => s + v, 0) / vals.length;
    return vals.reduce((s, v) => s + v, 0);
  };

  const cells: Record<string, Record<string, number>> = {};
  const rowTotals: Record<string, number> = {};
  const colTotals: Record<string, number> = {};
  let grandTotal = 0;

  for (const [rowKey, parts] of rowEntries) {
    const key = parts.join('|||');
    cells[key] = {};
    let rowSum = 0;

    for (const colKey of colKeys) {
      const val = aggregate(cellMap[rowKey]?.[colKey] || []);
      cells[key][colKey] = val;
      rowSum += val;
      colTotals[colKey] = (colTotals[colKey] || 0) + val;
    }

    rowTotals[key] = rowSum;
    grandTotal += rowSum;
  }

  return { rowKeys, colKeys, cells, rowTotals, colTotals, grandTotal };
}

/* ============================================================
   FORMATTING
   ============================================================ */

// fmt provided via useCurrency — use usePivotFmt inside components
function usePivotFmt() {
  const { fmt: cfmt } = useCurrency();
  return (val: number, compact = false): string => cfmt(val, 'EUR', { compact });
}

function valColor(val: number): string {
  if (val > 0) return 'text-emerald-600';
  if (val < 0) return 'text-red-500';
  return 'text-muted-foreground';
}

/* ============================================================
   CSV EXPORT
   ============================================================ */

function exportCSV(result: PivotResult, config: PivotConfig) {
  const rowDimHeaders = config.rowDims.length > 0 ? config.rowDims : ['Row'];
  const headers = [...rowDimHeaders, ...result.colKeys];
  if (config.showRowTotals) headers.push('Total');

  const lines: string[] = [headers.map(h => `"${h}"`).join(',')];

  for (const rowParts of result.rowKeys) {
    const key = rowParts.join('|||');
    const vals = result.colKeys.map(ck => String(result.cells[key]?.[ck] ?? 0));
    const row = [...rowParts.map(p => `"${p}"`), ...vals];
    if (config.showRowTotals) row.push(String(result.rowTotals[key] ?? 0));
    lines.push(row.join(','));
  }

  if (config.showColTotals) {
    const totalRow = [...rowDimHeaders.map(() => '"Total"')];
    for (const ck of result.colKeys) totalRow.push(String(result.colTotals[ck] ?? 0));
    if (config.showRowTotals) totalRow.push(String(result.grandTotal));
    lines.push(totalRow.join(','));
  }

  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `pivot-${config.dataSource}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/* ============================================================
   DIMENSION PICKER (add dimension dropdown)
   ============================================================ */

function DimPicker({
  available,
  onAdd,
}: {
  available: { key: string; label: string }[];
  onAdd: (key: string) => void;
}) {
  if (available.length === 0) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 text-muted-foreground">
          <Plus size={12} /> Add
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[140px]">
        {available.map(d => (
          <button
            key={d.key}
            className="w-full px-3 py-1.5 text-left text-sm hover:bg-accent rounded-sm"
            onClick={() => onAdd(d.key)}
          >
            {d.label}
          </button>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/* ============================================================
   FILTER DROPDOWN (multi-select checkbox)
   ============================================================ */

function FilterMultiSelect({
  dimKey,
  label,
  allValues,
  selected,
  onChange,
}: {
  dimKey: string;
  label: string;
  allValues: string[];
  selected: string[];
  onChange: (key: string, vals: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const sorted = useMemo(() => sortDimValues(dimKey, allValues), [dimKey, allValues]);
  const isAll = selected.length === 0;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <button className="w-full flex items-center justify-between text-xs py-1 px-1 hover:bg-accent rounded">
          <span className="truncate font-medium">{label}</span>
          <span className="flex items-center gap-1">
            {!isAll && (
              <Badge variant="secondary" className="text-[9px] h-4 px-1">
                {selected.length}
              </Badge>
            )}
            {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          </span>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="pl-1 max-h-40 overflow-y-auto">
        <button
          className="w-full flex items-center gap-2 text-xs py-0.5 px-1 hover:bg-accent rounded text-muted-foreground"
          onClick={() => onChange(dimKey, [])}
        >
          <Checkbox checked={isAll} className="h-3 w-3" />
          <span>All</span>
        </button>
        {sorted.map(v => {
          const checked = selected.includes(v);
          return (
            <button
              key={v}
              className="w-full flex items-center gap-2 text-xs py-0.5 px-1 hover:bg-accent rounded"
              onClick={() => {
                if (checked) {
                  onChange(dimKey, selected.filter(s => s !== v));
                } else {
                  onChange(dimKey, [...selected, v]);
                }
              }}
            >
              <Checkbox checked={checked} className="h-3 w-3" />
              <span className="truncate">{v}</span>
            </button>
          );
        })}
      </CollapsibleContent>
    </Collapsible>
  );
}

/**
 * Insertion point shown while dragging over a shelf. Row order defines the
 * pivot's grouping hierarchy, so dropping "somewhere in the middle" without a
 * marker made reordering a guess you only resolved after the drop — the index
 * was already being tracked in state, it just was never drawn.
 */
const InsertMarker = () => (
  <span className="w-0.5 self-stretch min-h-[18px] rounded-full bg-primary shrink-0" aria-hidden />
);

/* ============================================================
   MAIN COMPONENT
   ============================================================ */

export default function PivotWorkbench() {
  const fmt = usePivotFmt();
  const { data: rawData, loading, error, refresh } = useFetch<AnalyticsData>('/api/fin/analytics-data');

  const [config, setConfig] = useState<PivotConfig>({
    rowDims: ['entity'],
    colDims: ['month'],
    valueDim: 'amount',
    aggregation: 'sum',
    showRowTotals: true,
    showColTotals: true,
    showGrandTotal: true,
    filters: { year: ['2026'] },
    dataSource: 'cashflow',
  });

  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  // Sidebar closed by default so the pivot table gets full-width real
  // estate; users toggle it via the Filter button in the header on any
  // breakpoint. Previously this was hard-wired open on md+ via md:block.
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // View mode: 'statement' is the hardcoded Cash Flow Statement preset
  // (matches the Cashflow_Apr-May 2026 Excel layout); 'pivot' is the
  // generic drag-drop pivot table. Statement is default because it's
  // what treasury actually reviews.
  const [viewMode, setViewMode] = useState<'statement' | 'pivot'>('statement');
  const [dragOverTarget, setDragOverTarget] = useState<'available' | 'rows' | 'cols' | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const dragSourceRef = useRef<{ zone: 'available' | 'rows' | 'cols'; key: string } | null>(null);

  // Get current data array
  const sourceData = useMemo(() => {
    if (!rawData) return [];
    return rawData[config.dataSource] || [];
  }, [rawData, config.dataSource]);

  // Get all unique values for each dimension (for filters)
  const dimValues = useMemo(() => {
    const result: Record<string, string[]> = {};
    const dims = DIMENSIONS_BY_SOURCE[config.dataSource];
    for (const dim of dims) {
      const vals = new Set<string>();
      for (const row of sourceData) {
        const v = row[dim.key];
        if (v != null && v !== '') vals.add(String(v));
      }
      result[dim.key] = [...vals];
    }
    return result;
  }, [sourceData, config.dataSource]);

  // Compute pivot
  const pivotResult = useMemo(() => {
    if (sourceData.length === 0) return null;
    if (config.rowDims.length === 0 && config.colDims.length === 0) return null;
    return computePivot(sourceData, config);
  }, [sourceData, config]);

  // Helpers
  const dims = DIMENSIONS_BY_SOURCE[config.dataSource];
  const valueDims = VALUE_DIMS_BY_SOURCE[config.dataSource];

  const dimLabel = useCallback((key: string) => {
    return dims.find(d => d.key === key)?.label || key;
  }, [dims]);

  // Config updaters
  const updateConfig = useCallback((partial: Partial<PivotConfig>) => {
    setConfig(prev => ({ ...prev, ...partial }));
  }, []);

  const setDataSource = useCallback((ds: DataSourceKey) => {
    const newDims = DIMENSIONS_BY_SOURCE[ds];
    const newValueDims = VALUE_DIMS_BY_SOURCE[ds];
    const hasEntity = newDims.some(d => d.key === 'entity');
    const hasMonth = newDims.some(d => d.key === 'month');
    setConfig({
      rowDims: hasEntity ? ['entity'] : [newDims[0]?.key || ''],
      colDims: hasMonth ? ['month'] : newDims.length > 1 ? [newDims[1]?.key || ''] : [],
      valueDim: newValueDims[0]?.key || 'amount',
      aggregation: 'sum',
      showRowTotals: true,
      showColTotals: true,
      showGrandTotal: true,
      filters: ds === 'cashflow' ? { year: ['2026'] } : {},
      dataSource: ds,
    });
    setCollapsedGroups(new Set());
  }, []);

  const addRowDim = useCallback((key: string) => {
    updateConfig({ rowDims: [...config.rowDims, key] });
  }, [config.rowDims, updateConfig]);

  const removeRowDim = useCallback((key: string) => {
    updateConfig({ rowDims: config.rowDims.filter(d => d !== key) });
  }, [config.rowDims, updateConfig]);

  const addColDim = useCallback((key: string) => {
    updateConfig({ colDims: [...config.colDims, key] });
  }, [config.colDims, updateConfig]);

  const removeColDim = useCallback((key: string) => {
    updateConfig({ colDims: config.colDims.filter(d => d !== key) });
  }, [config.colDims, updateConfig]);

  const updateFilter = useCallback((dimKey: string, vals: string[]) => {
    setConfig(prev => ({
      ...prev,
      filters: { ...prev.filters, [dimKey]: vals },
    }));
  }, []);

  // Move a dimension from any zone to Rows at a specific index
  const moveDimToRows = useCallback((key: string, insertIndex?: number) => {
    setConfig(prev => {
      const newRowDims = prev.rowDims.filter(d => d !== key);
      const newColDims = prev.colDims.filter(d => d !== key);
      const idx = insertIndex ?? newRowDims.length;
      newRowDims.splice(idx, 0, key);
      return { ...prev, rowDims: newRowDims, colDims: newColDims };
    });
  }, []);

  // Move a dimension from any zone to Cols at a specific index
  const moveDimToCols = useCallback((key: string, insertIndex?: number) => {
    setConfig(prev => {
      const newColDims = prev.colDims.filter(d => d !== key);
      const newRowDims = prev.rowDims.filter(d => d !== key);
      const idx = insertIndex ?? newColDims.length;
      newColDims.splice(idx, 0, key);
      return { ...prev, rowDims: newRowDims, colDims: newColDims };
    });
  }, []);

  // Remove a dimension from both Rows and Cols (back to Available)
  const moveDimToAvailable = useCallback((key: string) => {
    setConfig(prev => ({
      ...prev,
      rowDims: prev.rowDims.filter(d => d !== key),
      colDims: prev.colDims.filter(d => d !== key),
    }));
  }, []);

  // All unused dimensions for the Available pool
  const availableDims = useMemo(() => {
    return dims.filter(d => !config.rowDims.includes(d.key) && !config.colDims.includes(d.key));
  }, [dims, config.rowDims, config.colDims]);

  // Drag & drop handlers
  const handleDragStart = useCallback((e: React.DragEvent, key: string, zone: 'available' | 'rows' | 'cols') => {
    e.dataTransfer.setData('text/plain', key);
    e.dataTransfer.effectAllowed = 'move';
    dragSourceRef.current = { zone, key };
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, zone: 'available' | 'rows' | 'cols', index?: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverTarget(zone);
    setDragOverIndex(index ?? null);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    // Only reset if we're leaving the zone entirely (not entering a child)
    const related = e.relatedTarget as HTMLElement | null;
    if (related && (e.currentTarget as HTMLElement).contains(related)) return;
    setDragOverTarget(null);
    setDragOverIndex(null);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, zone: 'available' | 'rows' | 'cols', index?: number) => {
    e.preventDefault();
    const key = e.dataTransfer.getData('text/plain');
    if (!key) return;
    if (zone === 'rows') {
      moveDimToRows(key, index);
    } else if (zone === 'cols') {
      moveDimToCols(key, index);
    } else {
      moveDimToAvailable(key);
    }
    setDragOverTarget(null);
    setDragOverIndex(null);
    dragSourceRef.current = null;
  }, [moveDimToRows, moveDimToCols, moveDimToAvailable]);

  // Hierarchical row grouping
  const groupedRows = useMemo(() => {
    if (!pivotResult || config.rowDims.length < 2) return null;

    const groups: { groupKey: string; rows: string[][] }[] = [];
    let currentGroup = '';
    let currentRows: string[][] = [];

    for (const rowParts of pivotResult.rowKeys) {
      const gk = rowParts[0];
      if (gk !== currentGroup) {
        if (currentRows.length > 0) {
          groups.push({ groupKey: currentGroup, rows: currentRows });
        }
        currentGroup = gk;
        currentRows = [];
      }
      currentRows.push(rowParts);
    }
    if (currentRows.length > 0) {
      groups.push({ groupKey: currentGroup, rows: currentRows });
    }

    return groups;
  }, [pivotResult, config.rowDims]);

  const toggleGroup = useCallback((groupKey: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupKey)) next.delete(groupKey);
      else next.add(groupKey);
      return next;
    });
  }, []);

  // Group totals for hierarchical mode
  const groupTotals = useMemo(() => {
    if (!pivotResult || !groupedRows) return {};
    const result: Record<string, { cols: Record<string, number>; total: number }> = {};

    for (const group of groupedRows) {
      const cols: Record<string, number> = {};
      let total = 0;
      for (const rowParts of group.rows) {
        const key = rowParts.join('|||');
        for (const ck of pivotResult.colKeys) {
          cols[ck] = (cols[ck] || 0) + (pivotResult.cells[key]?.[ck] || 0);
        }
        total += pivotResult.rowTotals[key] || 0;
      }
      result[group.groupKey] = { cols, total };
    }

    return result;
  }, [pivotResult, groupedRows]);

  /* ============================================================
     RENDER
     ============================================================ */

  if (loading && !rawData) {
    return (
      <div className="p-4 md:p-6 space-y-4">
        <Skeleton className="h-8 w-64" />
        <div className="flex gap-4">
          <Skeleton className="h-[500px] w-64 shrink-0" />
          <Skeleton className="h-[500px] flex-1" />
        </div>
      </div>
    );
  }

  if (error && !rawData) {
    return (
      <div className="p-4 md:p-6">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <BarChart3 size={32} className="text-amber-500 mb-3" />
            <p className="text-sm text-muted-foreground mb-3">{error}</p>
            <Button variant="outline" size="sm" onClick={refresh}>Retry</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 md:px-6 py-3 border-b bg-background shrink-0">
        <div className="flex items-center gap-3">
          {viewMode === 'pivot' && (
            <Button
              variant={sidebarOpen ? 'secondary' : 'ghost'}
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              title={sidebarOpen ? 'Hide filters panel' : 'Show filters panel'}
            >
              <Filter size={16} />
            </Button>
          )}
          <h1 className="text-lg font-semibold flex items-center gap-2">
            <Table2 size={20} /> Cash Flow
          </h1>
          {loading && (
            <Badge variant="secondary" className="text-[10px] animate-pulse">Loading...</Badge>
          )}
          {/* View mode toggle — Statement preset vs generic Pivot */}
          <div className="flex items-center rounded-md border bg-muted/30 p-0.5 ml-2">
            <Button
              variant={viewMode === 'statement' ? 'default' : 'ghost'}
              size="sm"
              className="h-7 px-2.5 text-xs gap-1.5"
              onClick={() => setViewMode('statement')}
            >
              <FileText size={13} /> Statement
            </Button>
            <Button
              variant={viewMode === 'pivot' ? 'default' : 'ghost'}
              size="sm"
              className="h-7 px-2.5 text-xs gap-1.5"
              onClick={() => setViewMode('pivot')}
            >
              <Table2 size={13} /> Pivot
            </Button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {pivotResult && (
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs gap-1"
              onClick={() => exportCSV(pivotResult, config)}
            >
              <Download size={14} /> Export
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={refresh}
          >
            <RefreshCw size={14} />
          </Button>
        </div>
      </div>

      {/* Body */}
      {viewMode === 'statement' ? (
        <StatementView />
      ) : (
      <div className="flex flex-1 overflow-hidden flex-col md:flex-row">
        {/* Sidebar */}
        <div
          className={`${sidebarOpen ? 'block' : 'hidden'} w-full md:w-64 shrink-0 border-b md:border-b-0 md:border-r bg-muted/30 overflow-y-auto p-3 space-y-4`}
        >
          {/* Data Source */}
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">
              Data Source
            </Label>
            <Select value={config.dataSource} onValueChange={(v) => setDataSource(v as DataSourceKey)}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(DATA_SOURCE_LABELS) as DataSourceKey[]).map(k => (
                  <SelectItem key={k} value={k}>{DATA_SOURCE_LABELS[k]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Separator />

          {/* Available Dimensions Pool */}
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">
              Available
            </Label>
            <div
              onDragOver={e => handleDragOver(e, 'available')}
              onDragLeave={handleDragLeave}
              onDrop={e => handleDrop(e, 'available')}
              className={cn(
                'min-h-[32px] border-2 border-dashed rounded-lg p-1.5 flex flex-wrap gap-1 transition-colors',
                dragOverTarget === 'available' ? 'border-primary bg-primary/5' : 'border-transparent'
              )}
            >
              {availableDims.length === 0 ? (
                <span className="text-[10px] text-muted-foreground italic px-1">All dimensions assigned</span>
              ) : (
                availableDims.map(d => (
                  <div
                    key={d.key}
                    draggable
                    role="button"
                    tabIndex={0}
                    aria-label={`Add ${d.label} to rows`}
                    onDragStart={e => handleDragStart(e, d.key, 'available')}
                    onClick={() => addRowDim(d.key)}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); addRowDim(d.key); } }}
                    className="px-2 py-1 rounded bg-muted cursor-grab text-xs font-medium border hover:bg-accent active:cursor-grabbing select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {d.label}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Row Dimensions (drop zone) */}
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">
              Rows
            </Label>
            <div
              onDragOver={e => handleDragOver(e, 'rows')}
              onDragLeave={handleDragLeave}
              onDrop={e => handleDrop(e, 'rows')}
              className={cn(
                'min-h-[40px] border-2 border-dashed rounded-lg p-2 flex flex-wrap gap-1 transition-colors',
                dragOverTarget === 'rows' ? 'border-primary bg-primary/5' : 'border-border'
              )}
            >
              {config.rowDims.map((d, i) => (
                <Fragment key={d}>
                  {dragOverTarget === 'rows' && dragOverIndex === i && <InsertMarker />}
                  <Badge
                    variant="secondary"
                    className="gap-1 text-[11px] cursor-grab active:cursor-grabbing select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    draggable
                    tabIndex={0}
                    aria-label={`Row dimension ${dimLabel(d)}`}
                    onKeyDown={(e: React.KeyboardEvent) => {
                      if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); removeRowDim(d); }
                    }}
                    onDragStart={(e: React.DragEvent) => handleDragStart(e, d, 'rows')}
                    onDragOver={(e: React.DragEvent) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setDragOverTarget('rows');
                      setDragOverIndex(i);
                    }}
                    onDrop={(e: React.DragEvent) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const key = e.dataTransfer.getData('text/plain');
                      if (key) moveDimToRows(key, i);
                      setDragOverTarget(null);
                      setDragOverIndex(null);
                    }}
                  >
                    <GripVertical size={10} className="text-muted-foreground" />
                    {dimLabel(d)}
                    <button
                      onClick={(e) => { e.stopPropagation(); removeRowDim(d); }}
                      tabIndex={-1}
                      className="ml-0.5 hover:text-destructive"
                    >
                      <X size={10} />
                    </button>
                  </Badge>
                </Fragment>
              ))}
              {dragOverTarget === 'rows' && dragOverIndex === null && config.rowDims.length > 0 && <InsertMarker />}
              {config.rowDims.length === 0 && (
                <span className="text-xs text-muted-foreground">Drop dimension here</span>
              )}
              <DimPicker available={availableDims} onAdd={addRowDim} />
            </div>
          </div>

          {/* Column Dimensions (drop zone) */}
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">
              Columns
            </Label>
            <div
              onDragOver={e => handleDragOver(e, 'cols')}
              onDragLeave={handleDragLeave}
              onDrop={e => handleDrop(e, 'cols')}
              className={cn(
                'min-h-[40px] border-2 border-dashed rounded-lg p-2 flex flex-wrap gap-1 transition-colors',
                dragOverTarget === 'cols' ? 'border-primary bg-primary/5' : 'border-border'
              )}
            >
              {config.colDims.map((d, i) => (
                <Fragment key={d}>
                  {dragOverTarget === 'cols' && dragOverIndex === i && <InsertMarker />}
                  <Badge
                    variant="secondary"
                    className="gap-1 text-[11px] cursor-grab active:cursor-grabbing select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    draggable
                    tabIndex={0}
                    aria-label={`Column dimension ${dimLabel(d)}`}
                    onKeyDown={(e: React.KeyboardEvent) => {
                      if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); removeColDim(d); }
                    }}
                    onDragStart={(e: React.DragEvent) => handleDragStart(e, d, 'cols')}
                    onDragOver={(e: React.DragEvent) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setDragOverTarget('cols');
                      setDragOverIndex(i);
                    }}
                    onDrop={(e: React.DragEvent) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const key = e.dataTransfer.getData('text/plain');
                      if (key) moveDimToCols(key, i);
                      setDragOverTarget(null);
                      setDragOverIndex(null);
                    }}
                  >
                    <GripVertical size={10} className="text-muted-foreground" />
                    {dimLabel(d)}
                    <button
                      onClick={(e) => { e.stopPropagation(); removeColDim(d); }}
                      tabIndex={-1}
                      className="ml-0.5 hover:text-destructive"
                    >
                      <X size={10} />
                    </button>
                  </Badge>
                </Fragment>
              ))}
              {dragOverTarget === 'cols' && dragOverIndex === null && config.colDims.length > 0 && <InsertMarker />}
              {config.colDims.length === 0 && (
                <span className="text-xs text-muted-foreground">Drop dimension here</span>
              )}
              <DimPicker available={availableDims} onAdd={addColDim} />
            </div>
          </div>

          <Separator />

          {/* Value & Aggregation */}
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">
              Values
            </Label>
            <Select value={config.valueDim} onValueChange={v => updateConfig({ valueDim: v })}>
              <SelectTrigger className="h-8 text-xs mb-1.5">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {valueDims.map(v => (
                  <SelectItem key={v.key} value={v.key}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={config.aggregation} onValueChange={v => updateConfig({ aggregation: v as AggregationType })}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="sum">Sum</SelectItem>
                <SelectItem value="count">Count</SelectItem>
                <SelectItem value="avg">Average</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Separator />

          {/* Filters */}
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 flex items-center gap-1">
              <Filter size={11} /> Filters
            </Label>
            <div className="space-y-0.5">
              {dims.map(d => {
                const vals = dimValues[d.key] || [];
                if (vals.length === 0) return null;
                return (
                  <FilterMultiSelect
                    key={d.key}
                    dimKey={d.key}
                    label={d.label}
                    allValues={vals}
                    selected={config.filters[d.key] || []}
                    onChange={updateFilter}
                  />
                );
              })}
            </div>
          </div>

          <Separator />

          {/* Settings */}
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 flex items-center gap-1">
              <Settings2 size={11} /> Settings
            </Label>
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <Checkbox
                  checked={config.showRowTotals}
                  onCheckedChange={v => updateConfig({ showRowTotals: !!v })}
                  className="h-3.5 w-3.5"
                />
                Row Totals
              </label>
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <Checkbox
                  checked={config.showColTotals}
                  onCheckedChange={v => updateConfig({ showColTotals: !!v })}
                  className="h-3.5 w-3.5"
                />
                Column Totals
              </label>
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <Checkbox
                  checked={config.showGrandTotal}
                  onCheckedChange={v => updateConfig({ showGrandTotal: !!v })}
                  className="h-3.5 w-3.5"
                />
                Grand Total
              </label>
            </div>
          </div>

          {/* Stats */}
          <div className="text-[10px] text-muted-foreground pt-2 border-t">
            {sourceData.length} records loaded
            {pivotResult && <> &middot; {pivotResult.rowKeys.length} rows &times; {pivotResult.colKeys.length} cols</>}
          </div>
        </div>

        {/* Pivot Table Area */}
        <div className="flex-1 overflow-auto p-4">
          {!pivotResult ? (
            <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
              {sourceData.length === 0
                ? 'No data available for this source. Try a different data source.'
                : 'Configure at least one row or column dimension to generate the pivot table.'
              }
            </div>
          ) : pivotResult.rowKeys.length === 0 ? (
            <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
              No data matches the current filters. Adjust filters to see results.
            </div>
          ) : (
            <div className="overflow-auto border rounded-lg">
              <table className="w-full text-sm border-collapse">
                {/* Header */}
                <thead className="sticky top-0 z-10 bg-muted/80 backdrop-blur">
                  {config.colDims.length >= 2 ? (() => {
                    // Multi-level column headers
                    const parentValues = [...new Set(pivotResult.colKeys.map(k => k.split('|||')[0]))];
                    const parentGroups = parentValues.map(parent => ({
                      label: parent,
                      childCount: pivotResult.colKeys.filter(k => k.startsWith(parent + '|||')).length,
                      children: pivotResult.colKeys.filter(k => k.startsWith(parent + '|||')).map(k => ({
                        key: k,
                        label: k.split('|||').slice(1).join(' / '),
                      })),
                    }));
                    const allChildren = parentGroups.flatMap(g => g.children);
                    return (
                      <>
                        {/* Parent level row */}
                        <tr className="bg-muted/80">
                          {config.rowDims.map(d => (
                            <th
                              key={d}
                              rowSpan={2}
                              className="text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider px-3 py-2.5 border-b border-r bg-muted whitespace-nowrap sticky left-0 z-10"
                            >
                              {dimLabel(d)}
                            </th>
                          ))}
                          {parentGroups.map(parent => (
                            <th
                              key={parent.label}
                              colSpan={parent.childCount}
                              className="text-center text-xs font-semibold uppercase tracking-wider px-3 py-2 border-b border-x whitespace-nowrap"
                            >
                              {parent.label}
                            </th>
                          ))}
                          {config.showRowTotals && (
                            <th rowSpan={2} className="text-right text-xs font-bold text-foreground uppercase tracking-wider px-3 py-2.5 border-b border-l bg-muted/60 whitespace-nowrap">
                              Total
                            </th>
                          )}
                        </tr>
                        {/* Child level row */}
                        <tr className="bg-muted/50">
                          {allChildren.map(child => (
                            <th
                              key={child.key}
                              className="text-right text-xs font-medium text-muted-foreground px-3 py-2 border-b border-x whitespace-nowrap min-w-[80px]"
                            >
                              {child.label}
                            </th>
                          ))}
                        </tr>
                      </>
                    );
                  })() : (
                    /* Single level headers */
                    <tr>
                      {config.rowDims.map((d, i) => (
                        <th
                          key={d}
                          className="text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider px-3 py-2.5 border-b border-r bg-muted whitespace-nowrap"
                        >
                          {dimLabel(d)}
                        </th>
                      ))}
                      {pivotResult.colKeys.map(ck => (
                        <th
                          key={ck}
                          className="text-right text-xs font-semibold text-muted-foreground uppercase tracking-wider px-3 py-2.5 border-b whitespace-nowrap min-w-[80px]"
                        >
                          {ck}
                        </th>
                      ))}
                      {config.showRowTotals && (
                        <th className="text-right text-xs font-bold text-foreground uppercase tracking-wider px-3 py-2.5 border-b border-l bg-muted/60 whitespace-nowrap">
                          Total
                        </th>
                      )}
                    </tr>
                  )}
                </thead>

                <tbody>
                  {/* Hierarchical mode (2+ row dims) */}
                  {groupedRows ? (
                    groupedRows.map(group => {
                      const isCollapsed = collapsedGroups.has(group.groupKey);
                      const gTotal = groupTotals[group.groupKey];

                      return (
                        <GroupRows
                          key={group.groupKey}
                          groupKey={group.groupKey}
                          rows={group.rows}
                          isCollapsed={isCollapsed}
                          onToggle={() => toggleGroup(group.groupKey)}
                          colKeys={pivotResult.colKeys}
                          cells={pivotResult.cells}
                          rowTotals={pivotResult.rowTotals}
                          groupTotals={gTotal}
                          showRowTotals={config.showRowTotals}
                          rowDims={config.rowDims}
                          dimLabel={dimLabel}
                        />
                      );
                    })
                  ) : (
                    /* Flat mode (single row dim) */
                    pivotResult.rowKeys.map((rowParts, ri) => {
                      const key = rowParts.join('|||');
                      return (
                        <tr
                          key={key}
                          className={`${ri % 2 === 0 ? 'bg-background' : 'bg-muted/20'} hover:bg-accent/40 transition-colors`}
                        >
                          {rowParts.map((p, pi) => (
                            <td
                              key={pi}
                              className="px-3 py-2 border-r font-medium text-foreground whitespace-nowrap"
                            >
                              {p}
                            </td>
                          ))}
                          {pivotResult.colKeys.map(ck => {
                            const val = pivotResult.cells[key]?.[ck] || 0;
                            return (
                              <td
                                key={ck}
                                className={`px-3 py-2 text-right font-mono text-xs tabular-nums ${val === 0 ? 'text-muted-foreground/40' : valColor(val)}`}
                              >
                                {val === 0 ? '-' : fmt(val, true)}
                              </td>
                            );
                          })}
                          {config.showRowTotals && (
                            <td className="px-3 py-2 text-right font-mono text-xs font-bold border-l bg-muted/30 tabular-nums">
                              {fmt(pivotResult.rowTotals[key] || 0, true)}
                            </td>
                          )}
                        </tr>
                      );
                    })
                  )}

                  {/* Column totals row */}
                  {config.showColTotals && (
                    <tr className="bg-muted/60 border-t-2 border-foreground/20 font-bold">
                      <td
                        className="px-3 py-2.5 border-r font-bold text-xs uppercase tracking-wider"
                        colSpan={config.rowDims.length}
                      >
                        Total
                      </td>
                      {pivotResult.colKeys.map(ck => (
                        <td
                          key={ck}
                          className={`px-3 py-2.5 text-right font-mono text-xs font-bold tabular-nums ${valColor(pivotResult.colTotals[ck] || 0)}`}
                        >
                          {fmt(pivotResult.colTotals[ck] || 0, true)}
                        </td>
                      ))}
                      {config.showRowTotals && config.showGrandTotal && (
                        <td className="px-3 py-2.5 text-right font-mono text-xs font-bold border-l bg-muted/40 tabular-nums">
                          {fmt(pivotResult.grandTotal, true)}
                        </td>
                      )}
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
}

/* ============================================================
   GROUP ROWS (hierarchical collapsible)
   ============================================================ */

function GroupRows({
  groupKey,
  rows,
  isCollapsed,
  onToggle,
  colKeys,
  cells,
  rowTotals,
  groupTotals,
  showRowTotals,
  rowDims,
  dimLabel,
}: {
  groupKey: string;
  rows: string[][];
  isCollapsed: boolean;
  onToggle: () => void;
  colKeys: string[];
  cells: Record<string, Record<string, number>>;
  rowTotals: Record<string, number>;
  groupTotals: { cols: Record<string, number>; total: number };
  showRowTotals: boolean;
  rowDims: string[];
  dimLabel: (k: string) => string;
}) {
  return (
    <>
      {/* Group header row */}
      <tr
        className="bg-muted/50 cursor-pointer hover:bg-accent/40 transition-colors border-t"
        onClick={onToggle}
      >
        <td className="px-3 py-2 border-r font-semibold text-foreground whitespace-nowrap" colSpan={rowDims.length}>
          <span className="flex items-center gap-1.5">
            {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
            {groupKey}
            <Badge variant="secondary" className="text-[9px] h-4 ml-1">{rows.length}</Badge>
          </span>
        </td>
        {colKeys.map(ck => {
          const val = groupTotals.cols[ck] || 0;
          return (
            <td
              key={ck}
              className={`px-3 py-2 text-right font-mono text-xs font-semibold tabular-nums ${val === 0 ? 'text-muted-foreground/40' : valColor(val)}`}
            >
              {val === 0 ? '-' : fmt(val, true)}
            </td>
          );
        })}
        {showRowTotals && (
          <td className="px-3 py-2 text-right font-mono text-xs font-bold border-l bg-muted/30 tabular-nums">
            {fmt(groupTotals.total, true)}
          </td>
        )}
      </tr>

      {/* Detail rows */}
      {!isCollapsed && rows.map((rowParts, ri) => {
        const key = rowParts.join('|||');
        return (
          <tr
            key={key}
            className={`${ri % 2 === 0 ? 'bg-background' : 'bg-muted/10'} hover:bg-accent/30 transition-colors`}
          >
            {rowParts.map((p, pi) => (
              <td
                key={pi}
                className={`px-3 py-1.5 border-r whitespace-nowrap text-xs ${
                  pi === 0 ? 'pl-8 text-muted-foreground' : 'font-medium text-foreground'
                }`}
              >
                {pi === 0 ? '' : p}
              </td>
            ))}
            {colKeys.map(ck => {
              const val = cells[key]?.[ck] || 0;
              return (
                <td
                  key={ck}
                  className={`px-3 py-1.5 text-right font-mono text-xs tabular-nums ${val === 0 ? 'text-muted-foreground/40' : valColor(val)}`}
                >
                  {val === 0 ? '-' : fmt(val, true)}
                </td>
              );
            })}
            {showRowTotals && (
              <td className="px-3 py-1.5 text-right font-mono text-xs font-medium border-l bg-muted/20 tabular-nums">
                {fmt(rowTotals[key] || 0, true)}
              </td>
            )}
          </tr>
        );
      })}
    </>
  );
}
